(() => {
  if (window.MOTMobileCollectionSwipeLoaded) return;
  window.MOTMobileCollectionSwipeLoaded = true;

  const mobile = window.matchMedia('(max-width: 1023px)');
  const desktop = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  const paginationLimit = 7;
  let gesture;
  let desktopAnimationSequence = 0;
  const stages = new WeakMap();
  const boundStages = new WeakSet();
  const pendingChanges = new WeakSet();

  const isInsideProductCarousel = (stage) => Boolean(stage.closest(
    '.splide, .swiper, .rp-marquee, [data-carousel-mask], [data-large-slider]'
  ));

  const activeImages = (stage) => {
    const activeSet = stage.querySelector(".mot-tile-variant-set[data-active='true']");
    if (activeSet) return Array.from(activeSet.querySelectorAll('.mot-tile-variant-swipe-image, template[data-mot-tile-image]'));
    return Array.from(stage.querySelectorAll('.mot-tile-generic-swipe-image, :scope > template[data-mot-tile-image]'));
  };

  const revealImage = (node) => {
    if (!node) return null;
    if (node.tagName !== 'TEMPLATE') return node;
    const content = node.content.cloneNode(true);
    const image = content.querySelector('img');
    if (image) {
      image.loading = 'eager';
      if (image.classList.contains('mot-tile-generic-extra-image')) {
        image.classList.add('mot-tile-generic-swipe-image');
      }
    }
    node.replaceWith(content);
    return image;
  };

  const prepareNextImage = (stage, index = 0) => {
    const images = activeImages(stage);
    if (images.length > 1) revealImage(images[(index + 1) % images.length]);
  };

  const removePagination = (stage) => {
    stage.querySelector(':scope > .mot-mobile-swipe-pagination')?.remove();
  };

  const removeDesktopControls = (stage) => {
    stage.querySelectorAll(':scope > .mot-desktop-gallery-arrow').forEach((arrow) => arrow.remove());
    stage.removeAttribute('data-mot-desktop-gallery-ready');
  };

  const arrowIcon = (direction) => {
    return `<span aria-hidden="true">${direction === 'previous' ? '&larr;' : '&rarr;'}</span>`;
  };

  const renderDesktopControls = (stage, count) => {
    if (count < 2) {
      removeDesktopControls(stage);
      return;
    }

    ['previous', 'next'].forEach((direction) => {
      let arrow = stage.querySelector(`:scope > .mot-desktop-gallery-arrow--${direction}`);
      if (arrow) return;
      arrow = document.createElement('button');
      arrow.type = 'button';
      arrow.className = `mot-desktop-gallery-arrow mot-desktop-gallery-arrow--${direction}`;
      arrow.setAttribute('aria-label', `${direction === 'previous' ? 'Previous' : 'Next'} product image`);
      arrow.innerHTML = arrowIcon(direction);
      stage.appendChild(arrow);
    });

    stage.dataset.motDesktopGalleryReady = 'true';
  };

  const renderPagination = (stage, count, activeIndex = 0) => {
    if (count < 2) {
      removePagination(stage);
      return;
    }

    let pagination = stage.querySelector(':scope > .mot-mobile-swipe-pagination');
    if (!pagination) {
      pagination = document.createElement('div');
      pagination.className = 'mot-mobile-swipe-pagination';
      pagination.setAttribute('aria-hidden', 'true');
      stage.appendChild(pagination);
    }

    const visibleCount = Math.min(count, paginationLimit);
    const windowStart = Math.max(
      0,
      Math.min(activeIndex - Math.floor(visibleCount / 2), count - visibleCount)
    );
    const windowEnd = windowStart + visibleCount;

    pagination.replaceChildren();
    for (let index = windowStart; index < windowEnd; index += 1) {
      const dot = document.createElement('span');
      dot.className = 'mot-mobile-swipe-pagination__dot';
      if (index === activeIndex) dot.classList.add('is-active');
      pagination.appendChild(dot);
    }
  };

  const reset = (stage) => {
    if (gesture?.stage === stage) gesture = undefined;
    const state = stages.get(stage);
    if (state) state.version = (state.version || 0) + 1;
    stage.querySelectorAll('.mot-mobile-swipe-current, .mot-mobile-swipe-animating, .mot-desktop-gallery-current, .mot-desktop-gallery-animating').forEach((image) => {
      if (image.classList.contains('mot-mobile-swipe-animating') || image.classList.contains('mot-desktop-gallery-animating')) {
        image.getAnimations?.().forEach((animation) => animation.cancel());
      }
      image.classList.remove(
        'mot-mobile-swipe-current',
        'mot-mobile-swipe-animating',
        'mot-desktop-gallery-current',
        'mot-desktop-gallery-animating'
      );
      image.style.removeProperty('transform');
    });
    stage.classList.remove('mot-desktop-gallery-active');
    delete stage.dataset.motDesktopGalleryIndex;
    delete stage.dataset.motDesktopGalleryAnimating;
    delete stage.dataset.motDesktopGalleryAnimationId;
    stage.dataset.motMobileSwipeIndex = '0';
    const images = activeImages(stage);
    if (images.length > 1 && !isInsideProductCarousel(stage)) stage.dataset.motMobileSwipeReady = 'true';
    else stage.removeAttribute('data-mot-mobile-swipe-ready');
    revealImage(images[0])?.classList.add('mot-mobile-swipe-current');
    if (desktop.matches || !isInsideProductCarousel(stage)) prepareNextImage(stage);
    if (mobile.matches && !isInsideProductCarousel(stage)) renderPagination(stage, images.length, 0);
    else removePagination(stage);
    if (desktop.matches) renderDesktopControls(stage, images.length);
    else removeDesktopControls(stage);
    if (state) state.activeSet = stage.querySelector(".mot-tile-variant-set[data-active='true']");
  };

  const showDesktopImage = (stage, requestedIndex) => {
    const images = activeImages(stage);
    if (!desktop.matches || images.length < 2) return;
    const index = (requestedIndex + images.length) % images.length;
    stage.querySelectorAll('.mot-desktop-gallery-current').forEach((image) => {
      image.classList.remove('mot-desktop-gallery-current');
    });
    revealImage(images[index])?.classList.add('mot-desktop-gallery-current');
    stage.dataset.motDesktopGalleryIndex = String(index);
    stage.classList.add('mot-desktop-gallery-active');
    prepareNextImage(stage, index);
  };

  const animateDesktopImage = (stage, requestedIndex, direction) => {
    const images = activeImages(stage);
    if (!desktop.matches || images.length < 2 || stage.dataset.motDesktopGalleryAnimating === 'true') return;

    const currentIndex = Number(stage.dataset.motDesktopGalleryIndex || 0);
    const nextIndex = (requestedIndex + images.length) % images.length;
    if (currentIndex === nextIndex) return;

    const outgoing = revealImage(images[currentIndex]);
    const incoming = revealImage(images[nextIndex]);
    if (!outgoing || !incoming) return;
    const width = stage.getBoundingClientRect().width;
    const duration = 300;
    const options = { duration, easing: 'ease', fill: 'both' };
    const animationId = String(++desktopAnimationSequence);

    stage.dataset.motDesktopGalleryAnimating = 'true';
    stage.dataset.motDesktopGalleryAnimationId = animationId;
    stage.classList.add('mot-desktop-gallery-active');
    outgoing.classList.add('mot-desktop-gallery-current', 'mot-desktop-gallery-animating');
    incoming.classList.add('mot-desktop-gallery-animating');

    const outgoingAnimation = outgoing.animate(
      [
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${-direction * width}px, 0, 0)` },
      ],
      options
    );
    const incomingAnimation = incoming.animate(
      [
        { transform: `translate3d(${direction * width}px, 0, 0)` },
        { transform: 'translate3d(0, 0, 0)' },
      ],
      options
    );

    Promise.allSettled([outgoingAnimation.finished, incomingAnimation.finished]).then(() => {
      outgoingAnimation.cancel();
      incomingAnimation.cancel();
      outgoing.classList.remove('mot-desktop-gallery-current', 'mot-desktop-gallery-animating');
      incoming.classList.remove('mot-desktop-gallery-animating');
      outgoing.style.removeProperty('transform');
      incoming.style.removeProperty('transform');
      if (stage.dataset.motDesktopGalleryAnimationId !== animationId) return;
      incoming.classList.add('mot-desktop-gallery-current');
      stage.dataset.motDesktopGalleryIndex = String(nextIndex);
      delete stage.dataset.motDesktopGalleryAnimating;
      delete stage.dataset.motDesktopGalleryAnimationId;
      prepareNextImage(stage, nextIndex);
    });
  };

  const activateDesktopGallery = (stage) => {
    if (!desktop.matches || activeImages(stage).length < 2) return;
    const storedIndex = stage.dataset.motDesktopGalleryIndex;
    showDesktopImage(stage, storedIndex == null ? 1 : Number(storedIndex));
  };

  const deactivateDesktopGallery = (stage) => {
    stage.querySelectorAll('.mot-desktop-gallery-animating').forEach((image) => {
      image.getAnimations?.().forEach((animation) => animation.cancel());
      image.classList.remove('mot-desktop-gallery-animating');
      image.style.removeProperty('transform');
    });
    stage.classList.remove('mot-desktop-gallery-active');
    stage.querySelectorAll('.mot-desktop-gallery-current').forEach((image) => {
      image.classList.remove('mot-desktop-gallery-current');
    });
    delete stage.dataset.motDesktopGalleryIndex;
    delete stage.dataset.motDesktopGalleryAnimating;
    delete stage.dataset.motDesktopGalleryAnimationId;
  };

  const clearDragImage = (image) => {
    if (!image) return;
    image.classList.remove('mot-mobile-swipe-animating');
    image.style.removeProperty('transform');
  };

  const settleGesture = (current, complete) => {
    if (!current?.incoming || !current?.outgoing) return;
    const { stage, outgoing, incoming, direction, width, dx, nextIndex } = current;
    const version = stages.get(stage)?.version;
    const outgoingTarget = complete ? -direction * width : 0;
    const incomingTarget = complete ? 0 : direction * width;
    const remaining = complete ? Math.max(0, width - Math.abs(dx)) : Math.abs(dx);
    const duration = Math.max(110, Math.min(210, remaining * 0.65));
    const options = { duration, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'both' };
    const outgoingAnimation = outgoing.animate(
      [{ transform: `translateX(${dx}px)` }, { transform: `translateX(${outgoingTarget}px)` }],
      options
    );
    const incomingStart = dx + direction * width;
    const incomingAnimation = incoming.animate(
      [{ transform: `translateX(${incomingStart}px)` }, { transform: `translateX(${incomingTarget}px)` }],
      options
    );

    Promise.allSettled([outgoingAnimation.finished, incomingAnimation.finished]).then(() => {
      outgoingAnimation.cancel();
      incomingAnimation.cancel();
      if (version !== stages.get(stage)?.version) return;
      if (complete) {
        outgoing.classList.remove('mot-mobile-swipe-current');
        incoming.classList.add('mot-mobile-swipe-current');
        stage.dataset.motMobileSwipeIndex = String(nextIndex);
        renderPagination(stage, activeImages(stage).length, nextIndex);
        prepareNextImage(stage, nextIndex);
      }
      clearDragImage(outgoing);
      clearDragImage(incoming);
    });
  };

  const initialiseStage = (stage) => {
      const state = stages.get(stage);
      if (state?.ready) return;
      stages.set(stage, { ready: true });
      stageObserver?.unobserve(stage);
      const insideProductCarousel = isInsideProductCarousel(stage);

      const hasVariantGallery = stage.querySelector('.mot-tile-variant-set');
      if (!hasVariantGallery) {
        const genericImages = Array.from(stage.querySelectorAll('img')).filter((image) => (
          !image.classList.contains('mot-tile-variant-primary-image') &&
          !image.classList.contains('mot-tile-variant-hover-image') &&
          !image.classList.contains('mot-tile-variant-extra-image')
        ));
        if (genericImages.length > 1 || stage.querySelector(':scope > template[data-mot-tile-image]')) {
          genericImages.forEach((image) => image.classList.add('mot-tile-generic-swipe-image'));
        }
      }
      if (activeImages(stage).length > 1 && !insideProductCarousel) {
        stage.dataset.motMobileSwipeReady = 'true';
      } else {
        stage.removeAttribute('data-mot-mobile-swipe-ready');
      }
      reset(stage);

      if (!boundStages.has(stage)) {
        stage.addEventListener('mouseenter', () => activateDesktopGallery(stage));
        stage.addEventListener('mouseleave', () => deactivateDesktopGallery(stage));
        boundStages.add(stage);
        stage.dataset.motDesktopGalleryBound = 'true';
      }
  };

  const stageObserver = 'IntersectionObserver' in window && !window.Shopify?.designMode
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) initialiseStage(entry.target);
        });
      }, { rootMargin: '200px 0px' })
    : null;

  const initialise = (root = document) => {
    root.querySelectorAll('[data-mot-tile] .mot-tile-image-stage').forEach((stage) => {
      if (stages.has(stage)) return;
      stages.set(stage, { ready: false });
      if (stageObserver) stageObserver.observe(stage);
      else initialiseStage(stage);
    });
  };

  document.addEventListener('pointerover', (event) => {
    if (!desktop.matches) return;
    const stage = event.target.closest('.mot-tile-image-stage');
    if (!stage || stage.contains(event.relatedTarget)) return;
    initialiseStage(stage);
    activateDesktopGallery(stage);
  });

  document.addEventListener('focusin', (event) => {
    const tile = event.target.closest('[data-mot-tile]');
    const stage = tile?.querySelector('.mot-tile-image-stage');
    if (stage) initialiseStage(stage);
  });

  document.addEventListener('pointerdown', (event) => {
    if (!mobile.matches || !event.isPrimary) return;
    const stage = event.target.closest('.mot-tile-image-stage');
    if (!stage) return;
    initialiseStage(stage);
    if (isInsideProductCarousel(stage) || activeImages(stage).length < 2) return;
    gesture = {
      stage,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      width: stage.getBoundingClientRect().width,
      images: activeImages(stage),
      oldIndex: Number(stage.dataset.motMobileSwipeIndex || 0),
      dx: 0,
    };
    stage.setPointerCapture?.(event.pointerId);
  });

  document.addEventListener('pointermove', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (Math.abs(dy) > Math.abs(dx) && !gesture.incoming) return;
    if (Math.abs(dx) < 3) return;
    event.preventDefault();

    const direction = dx < 0 ? 1 : -1;
    const nextIndex = (gesture.oldIndex + direction + gesture.images.length) % gesture.images.length;
    const incoming = revealImage(gesture.images[nextIndex]);
    const outgoing = revealImage(gesture.images[gesture.oldIndex]);
    gesture.images[nextIndex] = incoming;
    gesture.images[gesture.oldIndex] = outgoing;
    if (!incoming || !outgoing) return;
    if (gesture.incoming && gesture.incoming !== incoming) clearDragImage(gesture.incoming);

    gesture.direction = direction;
    gesture.nextIndex = nextIndex;
    gesture.incoming = incoming;
    gesture.outgoing = outgoing;
    gesture.dx = dx;
    outgoing.classList.add('mot-mobile-swipe-animating');
    incoming.classList.add('mot-mobile-swipe-animating');
    outgoing.style.transform = `translateX(${dx}px)`;
    incoming.style.transform = `translateX(${dx + direction * gesture.width}px)`;
  });

  document.addEventListener('pointerup', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const current = gesture;
    gesture = undefined;
    if (!current.incoming) return;
    const complete = Math.abs(current.dx) >= Math.min(45, current.width * 0.18);
    settleGesture(current, complete);
    current.stage.dataset.motJustSwiped = 'true';
    setTimeout(() => delete current.stage.dataset.motJustSwiped, 350);
  });

  document.addEventListener('pointercancel', () => {
    const current = gesture;
    gesture = undefined;
    settleGesture(current, false);
  });

  document.addEventListener('change', (event) => {
    const swatch = event.target.closest("[data-mot-tile] input[type='radio'][data-variant-id]");
    if (!swatch) return;
    const stage = swatch.closest('[data-mot-tile]')?.querySelector('.mot-tile-image-stage');
    if (stage && !pendingChanges.has(stage)) {
      pendingChanges.add(stage);
      requestAnimationFrame(() => {
      pendingChanges.delete(stage);
      if (!stage.isConnected) return;
      const state = stages.get(stage);
      if (!state?.ready) {
        if (swatch.isConnected && document.activeElement === swatch) initialiseStage(stage);
        return;
      }
      const activeSet = stage.querySelector(".mot-tile-variant-set[data-active='true']");
      if (state.activeSet === activeSet) return;
      reset(stage);
      if (desktop.matches && stage.matches(':hover')) activateDesktopGallery(stage);
      });
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (!event.target.closest('.mot-desktop-gallery-arrow')) return;
    event.stopPropagation();
  });

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('.mot-desktop-gallery-arrow');
    if (arrow) {
      event.preventDefault();
      event.stopPropagation();
      const stage = arrow.closest('.mot-tile-image-stage');
      if (!stage || !desktop.matches) return;
      activateDesktopGallery(stage);
      const currentIndex = Number(stage.dataset.motDesktopGalleryIndex || 0);
      const direction = arrow.classList.contains('mot-desktop-gallery-arrow--previous') ? -1 : 1;
      animateDesktopImage(stage, currentIndex + direction, direction);
      arrow.blur();
      return;
    }

    const stage = event.target.closest('.mot-tile-image-stage');
    if (stage?.dataset.motJustSwiped === 'true') {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  document.addEventListener('dragstart', (event) => {
    if (event.target.closest(".mot-tile-image-stage[data-mot-mobile-swipe-ready='true']")) event.preventDefault();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initialise();
  });
  const updateLayout = () => {
    document.querySelectorAll('[data-mot-tile] .mot-tile-image-stage').forEach((stage) => {
      if (stages.get(stage)?.ready) reset(stage);
    });
  };
  mobile.addEventListener('change', updateLayout);
  desktop.addEventListener('change', updateLayout);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.mot-tile-image-stage').forEach((stage) => {
      stageObserver?.unobserve(stage);
      stages.delete(stage);
    });
  });
})();
